import {
	useState,
	useEffect,
	useRef,
	forwardRef,
	useImperativeHandle,
} from 'react'
import Style from './style'

const InfiniteSelect = forwardRef(
	(
		{
			defaultValue,
			onChange,
			options = [],
			isLoading = false,
			loadMore,
			hasMore,
			disabled = false,
			title = '',
			width = '100%',
		},
		ref
	) => {
		const [internalValue, setInternalValue] = useState(defaultValue || 'all')
		const [isOpen, setIsOpen] = useState(false)
		const dropdownRef = useRef(null)
		const listRef = useRef(null)

		// Expose value through ref
		useImperativeHandle(ref, () => ({
			value: internalValue,
		}))

		// Sync internal value with defaultValue changes
		useEffect(() => {
			setInternalValue(defaultValue || 'all')
		}, [defaultValue])

		const selectedOption = options.find((opt) => String(opt.value) === String(internalValue))

		useEffect(() => {
			const handleClickOutside = (event) => {
				if (
					dropdownRef.current &&
					!dropdownRef.current.contains(event.target)
				) {
					setIsOpen(false)
				}
			}

			document.addEventListener('mousedown', handleClickOutside)
			return () => {
				document.removeEventListener('mousedown', handleClickOutside)
			}
		}, [])

		const handleScroll = (e) => {
			const {scrollTop, clientHeight, scrollHeight} = e.target
			if (scrollHeight - scrollTop <= clientHeight + 50) {
				if (!isLoading && hasMore && loadMore) {
					loadMore()
				}
			}
		}

		const handleSelect = (optionValue) => {
			setInternalValue(optionValue)
			if (onChange) {
				onChange(optionValue)
			}
			setIsOpen(false)
		}

		return (
			<Style.Wrapper width={width} ref={dropdownRef}>
				{title && <Style.Title>{title}</Style.Title>}
				<Style.Container onClick={() => !disabled && setIsOpen(!isOpen)}>
					<Style.SelectedValue>{selectedOption?.label}</Style.SelectedValue>
					<Style.Arrow $isOpen={isOpen} />
				</Style.Container>
				{isOpen && (
					<Style.DropdownList onScroll={handleScroll} ref={listRef}>
						{options.map((option, index) => (
							<Style.OptionItem
								key={`${option.value}-${index}`}
								onClick={() => handleSelect(option.value)}
								$isSelected={String(internalValue) === String(option.value)}
							>
								{option.label}
							</Style.OptionItem>
						))}
						{isLoading && <Style.LoadingItem>Loading...</Style.LoadingItem>}
					</Style.DropdownList>
				)}
			</Style.Wrapper>
		)
	}
)

export default InfiniteSelect
